/**
 * selectFx.min.js v1.0.2.5
 * converted SelectFX to ES Module and added handler to Optgroups
 */
class SelectFx {
    constructor(el, options) {
        this.el = el;
        this.options = Object.assign({}, this.defaultOptions, options);
        this._init();
    }

    get defaultOptions() {
        return {
            newTab: true,
            stickyPlaceholder: true,
            onChange: () => false,
        };
    }

    _init() {
        this.hasDefaultPlaceholder = this.el.querySelector('option[selected]')?.disabled;
        this.selectedOpt = this.el.querySelector('option[selected]') || this.el.querySelector('option');
        this._createSelectEl();
        this.selOpts = Array.from(this.selEl.querySelectorAll('li[data-option]'));
        this.selOptsCount = this.selOpts.length;
        this.current = this.selOpts.indexOf(this.selEl.querySelector('li.selected')) || -1;
        this.selPlaceholder = this.selEl.querySelector('span.placeholder');
        this._initEvents();
    }

    _createSelectEl() {
        const optionsFragment = document.createDocumentFragment();

        const createOptionHtml = (option) => {
            const icon = option.getAttribute('data-icon');
            const isSelected = option.hasAttribute('selected');
            const className = ` ${isSelected ? 'selected' : ''}` + (icon ? ' contain-icon' : '');
            const imgHtml = icon ? `<img src="${icon}" alt="${option.textContent}" loading="lazy"/>` : '';
            return `<li class="${className.trim()}" data-option data-value="${option.value}">${imgHtml}<span>${option.textContent}</span></li>`;
        };

        Array.from(this.el.children).forEach(child => {
            if (!child.disabled) {
                const tagName = child.tagName.toLowerCase();

                if (tagName === 'option') {
                    // Handle regular option elements
                    const optionElement = new DOMParser().parseFromString(createOptionHtml(child), 'text/html').body.firstChild;
                    optionsFragment.appendChild(optionElement);
                } else if (tagName === 'optgroup') {
                    // Handle optgroup elements
                    const optgroupElement = document.createElement('li');
                    optgroupElement.className = 'optgroup';
                    optgroupElement.innerHTML = `<span>${child.label}</span><ul></ul>`;

                    Array.from(child.children).forEach(option => {
                        if (!option.disabled) {
                            const optionElement = new DOMParser().parseFromString(createOptionHtml(option), 'text/html').body.firstChild;
                            optgroupElement.querySelector('ul').appendChild(optionElement);
                        }
                    });

                    optionsFragment.appendChild(optgroupElement);
                }
            }
        });

        // Create the custom select container
        this.selEl = document.createElement('div');
        this.selEl.className = this.el.className;
        this.selEl.tabIndex = this.el.tabIndex;
        this.selEl.innerHTML = `
		            <span class="placeholder">${this.selectedOpt.textContent}</span>
		            <div class="options" data-simplebar><ul></ul></div>
		        `;

        // Append the generated options to the custom select
        this.selEl.querySelector('.options ul').appendChild(optionsFragment);

        // Insert the custom select into the DOM and hide the original <select>
        this.el.style.display = 'none';
        this.el.parentNode.insertBefore(this.selEl, this.el.nextSibling);
    }

    _initEvents() {
        this.selPlaceholder.addEventListener('click', () => this._toggleSelect());

        this.selOpts.forEach((opt, index) => {
            opt.addEventListener('click', () => {
                this.current = index;
                this._changeOption();
                this._toggleSelect();
            });
        });

        document.addEventListener('click', (e) => {
            if (this._isOpen() && !this.selEl.contains(e.target)) {
                this._toggleSelect();
            }
        });

        this.selEl.addEventListener('keydown', (e) => this._handleKeydown(e));
    }

    _handleKeydown(e) {
        switch (e.keyCode) {
            case 38: // Up arrow
                e.preventDefault();
                this._navigateOpts('prev');
                break;
            case 40: // Down arrow
                e.preventDefault();
                this._navigateOpts('next');
                break;
            case 32: // Space
                e.preventDefault();
                if (this._isOpen() && this.preSelCurrent !== -1) {
                    this._changeOption();
                }
                this._toggleSelect();
                break;
            case 13: // Enter
                e.preventDefault();
                if (this._isOpen() && this.preSelCurrent !== -1) {
                    this._changeOption();
                    this._toggleSelect();
                }
                break;
            case 27: // Escape
                e.preventDefault();
                if (this._isOpen()) this._toggleSelect();
                break;
        }
    }

    _navigateOpts(direction) {
        if (!this._isOpen()) this._toggleSelect();

        let currentIndex = this.preSelCurrent !== undefined ? this.preSelCurrent : this.current;
        if (direction === 'prev' && currentIndex > 0) {
            this.preSelCurrent = currentIndex - 1;
        } else if (direction === 'next' && currentIndex < this.selOptsCount - 1) {
            this.preSelCurrent = currentIndex + 1;
        }

        this._removeFocus();
        this.selOpts[this.preSelCurrent]?.classList.add('focus');
    }

    _toggleSelect() {
        this._removeFocus();
        if (this._isOpen()) {
            this.selPlaceholder.innerHTML = this.current !== -1 ? this.selOpts[this.current].innerHTML : this.selectedOpt.innerHTML;
            this.selEl.classList.remove('active');
        } else {
            if (this.hasDefaultPlaceholder && this.options.stickyPlaceholder) {
                this.selPlaceholder.innerHTML = this.selectedOpt.innerHTML;
            }
            this.selEl.classList.add('active');
        }
    }

    _changeOption() {
        if (this.preSelCurrent !== undefined && this.preSelCurrent !== -1) {
            this.current = this.preSelCurrent;
            this.preSelCurrent = -1;
        }

        if (this.current < 0 || this.current >= this.selOpts.length) return;

        const selectedOption = this.selOpts[this.current];

        if (!selectedOption) return;

        // Find the corresponding <option> in the original <select>
        const optionElement = this.el.querySelector(`option[value="${selectedOption.getAttribute('data-value')}"]`);

        if (!optionElement) return;

        // Get the icon URL and text for the selected option
        const iconSrc = optionElement.getAttribute('data-icon');
        const iconHtml = iconSrc ? `<img src="${iconSrc}" alt="Icon" />` : '';
        const newValue = `${iconHtml}<span>${selectedOption.textContent}</span>`;

        // Update the placeholder with icon and text
        this.selPlaceholder.innerHTML = newValue;

        // Update the native select value
        this.el.value = optionElement.value;

        // Update the selected class
        const previousSelected = this.selEl.querySelector('li.selected');
        if (previousSelected) previousSelected.classList.remove('selected');
        selectedOption.classList.add('selected');

        // Optionally handle link opening
        const dataLink = optionElement.getAttribute('data-link');
        if (dataLink) {
            this.options.newTab ? window.open(dataLink, '_blank') : (window.location = dataLink);
        }

        // Trigger onChange callback
        this.options.onChange(this.el.value);
    }

    _isOpen() {
        return this.selEl.classList.contains('active');
    }

    _removeFocus() {
        const focusedOption = this.selEl.querySelector('li.focus');
        if (focusedOption) focusedOption.classList.remove('focus');
    }

    setValue(value) {
        const option = this.el.querySelector(`option[value="${value}"]`);
        if (option) {
            this.el.value = option.value;
            const selectedOpt = this.selEl.querySelector('.selected');
            if (selectedOpt) selectedOpt.classList.remove('selected');
            const newSelectedOpt = this.selEl.querySelector(`[data-value="${value}"]`);
            if (newSelectedOpt) {
                newSelectedOpt.classList.add('selected');
                this.selPlaceholder.textContent = newSelectedOpt.textContent;
            }
        }
    }

    getValue() {
        return this.el.value;
    }
}
export default SelectFx;
